/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useState } from 'react';
import { MuiThemeProvider } from '@material-ui/core/styles';
import Autocomplete, {
    createFilterOptions,
} from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import theme from 'utils/theme';
import '../Input.css';
import './Suggester.css';
import createOnChangeTextFieldFactory from './createOnChangeTextFieldFactory';

const suggesterClasses = {
    root: 'Suggester__root',
    tag: 'Suggester__tag',
};

const inputClasses = {
    root: 'Input__container',
    input: 'Input__input',
    underline: 'Input__container--underline',
    focused: 'Input__container--focused',
    disabled: 'Input__container--disabled',
};

const labelClasses = {
    root: 'Input__label',
    focused: 'Input__label--focused',
    disabled: 'Input__label--disabled',
};

const filter = createFilterOptions();

const forbiddenChars = [','];

function includesForbiddenChars(paste) {
    return forbiddenChars.some((forbiddenChar) =>
        paste.includes(forbiddenChar)
    );
}

function ImprovedSuggester({
    value = [],
    options = [],
    id,
    label,
    placeholder,
    noOptionsText,
    debounceDelay = 0,
    loading,
    loadingText,
    onChange,
    onFocus,
    onSelect,
    required,
}) {
    const [inputValue, setInputValue] = useState('');

    const onChangeTextFieldFactory = useMemo(
        () => createOnChangeTextFieldFactory(),
        []
    );

    return (
        <MuiThemeProvider theme={theme}>
            <Autocomplete
                value={value}
                inputValue={inputValue}
                multiple
                id={id}
                className="Suggester"
                options={options}
                defaultValue={null}
                freeSolo
                filterSelectedOptions
                filterOptions={(options2, params) => {
                    const filtered = filter(options2, params);

                    // Suggest the creation of a new value
                    if (
                        params.inputValue !== '' &&
                        options2.filter(
                            (x) =>
                                x.toUpperCase() ===
                                params.inputValue.toUpperCase()
                        ).length === 0 &&
                        value.filter(
                            (x) =>
                                x.toUpperCase() ===
                                params.inputValue.toUpperCase()
                        ).length === 0
                    ) {
                        filtered.push(params.inputValue);
                    }

                    return filtered;
                }}
                disableClearable
                noOptionsText={noOptionsText}
                popupIcon={null}
                loading={loading}
                loadingText={loadingText}
                classes={suggesterClasses}
                onChange={(event, newValue, reason) => {
                    onSelect(event, newValue, reason);
                    setInputValue('');
                }}
                renderInput={(params) => (
                    <TextField
                        className="Input Input--transparent"
                        {...params}
                        value={inputValue}
                        variant="filled"
                        label={label}
                        placeholder={placeholder}
                        InputProps={{
                            ...params.InputProps,
                            classes: inputClasses,
                        }}
                        InputLabelProps={{
                            classes: labelClasses,
                        }}
                        onFocus={onFocus}
                        onKeyDown={(event) => {
                            if (includesForbiddenChars(event.key)) {
                                event.preventDefault();
                                return false;
                            }
                            return true;
                        }}
                        onPaste={(event) => {
                            const paste = (
                                event.clipboardData || window.clipboardData
                            ).getData('text');
                            if (includesForbiddenChars(paste)) {
                                event.preventDefault();
                                return false;
                            }
                            return true;
                        }}
                        onChange={onChangeTextFieldFactory(
                            onChange,
                            debounceDelay,
                            setInputValue
                        )}
                        required={required && value.length === 0}
                    />
                )}
            />
        </MuiThemeProvider>
    );
}

export default ImprovedSuggester;
